import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { legacyTheme, styled } from '@prose-ui/legacy';

import { CountrySelect } from 'Components/CountrySelect';
import { TextField } from 'Components/TextField';
import { Typography } from 'Components/Typography';

import { zipcodeServerError } from '../constants/errorMessages';

import consultationCategoryProp from 'PropTypes/consultationProps';
import { httpErrorProp } from 'PropTypes/errorProps';

import { formatZipcodeByCountryCode } from 'utils/textUtils';
import { isValidZipCodeByCountry } from 'utils/validators';

import QuestionLayout from './QuestionLayout';

const QuestionContainer = styled.div`
  flex: 1 0 auto;
  max-width: ${legacyTheme.width.narrow};
`;

const CountrySelectorContainer = styled.div`
  margin-top: 48px;
`;

const ServerError = styled(Typography)`
  flex: 3;
`;

const propsByCountry = {
  US: {
    maxLength: 5,
    type: 'tel',
  },
  CA: {
    maxLength: 7,
    type: undefined,
  },
};

const ZipCode = ({
  consultationCategory,
  initialValue,
  initialCountryValue,
  invalidZipcode,
  onNext,
  countries,
  serverError,
  hasCompletedOneOfConsultation,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);
  /**
   * Edge case n dev mode: window.PROSE_GUESSED_USER_COUNTRY is not set at initial load for Canada
   * To set it, the dropdown has to be changed once (click)
   */
  const [selectedCountry, setSelectedCountry] = useState(initialCountryValue);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const textFieldProps = propsByCountry[selectedCountry];

  const onChangeZipcode = event =>
    setValue(
      formatZipcodeByCountryCode(
        { value: event.target.value, previousValue: value },
        selectedCountry
      )
    );

  useEffect(() => {
    setError(isValidZipCodeByCountry(value)[selectedCountry] ? '' : '(Invalid ZIP Code)');
  }, [value]);

  // the invalidZipcode prop will be toggled if there is a server validation error
  useEffect(() => {
    if (invalidZipcode) {
      setError('(US and Canada ZIP Code only)');
    }
  }, [invalidZipcode]);

  const shippingNotice = hasCompletedOneOfConsultation
    ? 'This ZIP code will be used in both your hair and skincare consultation. For now, we only ship to the US & Canada.'
    : 'For now, we only ship to the US and Canada.';

  return (
    <QuestionLayout
      consultationCategory={consultationCategory}
      disabled={!isValidZipCodeByCountry(value)[selectedCountry]}
      submit={() => onNext({ profile_zipcode: value, profile_country: selectedCountry })}
      {...props}
    >
      <QuestionContainer>
        <CountrySelectorContainer>
          <CountrySelect
            list={countries}
            onChange={event => setSelectedCountry(event.target.value)}
            testId="consultation-country-select"
            value={selectedCountry}
          />
        </CountrySelectorContainer>
        <TextField
          error={touched ? error : ''}
          id="zipcode"
          label="Zip Code"
          margin
          maxLength={textFieldProps?.maxLength}
          name="zipcode"
          onBlur={() => setTouched(true)}
          onChange={onChangeZipcode}
          type={textFieldProps?.type}
          value={value}
        />
        {/* Content should be moved to the `body` prop as soon as supplements is merged into `main` branch */}
        <Typography align="center" color="grey" variant="p2">
          {shippingNotice}
        </Typography>
      </QuestionContainer>
      {serverError && (
        <ServerError align="center" color="rouge" data-testid="zipcode-server-error" variant="p2">
          {zipcodeServerError}
        </ServerError>
      )}
    </QuestionLayout>
  );
};

ZipCode.defaultProps = {
  initialValue: '',
  countries: [{ label: '', value: '' }],
  serverError: null,
  hasCompletedOneOfConsultation: false,
};

ZipCode.propTypes = {
  consultationCategory: consultationCategoryProp.isRequired,
  initialCountryValue: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape()),
  initialValue: PropTypes.string,
  invalidZipcode: PropTypes.bool,
  isSaving: PropTypes.bool.isRequired,
  onNext: PropTypes.func.isRequired,
  serverError: httpErrorProp,
  hasCompletedOneOfConsultation: PropTypes.bool,
};

export default ZipCode;
